import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import DiscoverWidget from "@/components/DiscoverWidget";
import ImageUploader from "@/components/ImageUploader";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    Plus,
    Search,
    Edit,
    Trash2,
    Eye,
    FileText,
    Sparkles,
    Save,
    X,
    Image as ImageIcon
} from "lucide-react";
import EditImageDialog from "@/components/admin/EditImageDialog";

const CATEGORIES = [
    { id: "football", name: "Football" },
    { id: "basketball", name: "Basketball" },
    { id: "athletics", name: "Athletics" },
    { id: "rugby", name: "Rugby" },
    { id: "cricket", name: "Cricket" },
    { id: "tennis", name: "Tennis" },
    { id: "boxing", name: "Boxing" },
    { id: "other", name: "Other" },
];

const CONTENT_TYPES = [
    { id: "news", name: "News" },
    { id: "team_news", name: "Team News" },
    { id: "pre_match", name: "Pre-Match" },
    { id: "match_report", name: "Match Report" },
    { id: "post_match", name: "Post-Match" },
    { id: "analysis", name: "Analysis" },
    { id: "transfer", name: "Transfer" },
    { id: "injury_update", name: "Injury Update" },
    { id: "standings", name: "Standings" },
    { id: "fixtures", name: "Fixtures" },
    { id: "highlights", name: "Highlights" },
    { id: "interview", name: "Interview" },
    { id: "opinion", name: "Opinion" },
];

const COMPETITIONS = {
    football: [
        { id: "epl", name: "Premier League (EPL)" },
        { id: "ucl", name: "Champions League" },
        { id: "laliga", name: "La Liga" },
        { id: "bundesliga", name: "Bundesliga" },
        { id: "serie_a", name: "Serie A" },
        { id: "uel", name: "Europa League" },
        { id: "caf_cl", name: "CAF Champions League" },
        { id: "afcon", name: "AFCON" },
        { id: "nbc_premier", name: "NBC Premier League (TZ)" },
    ],
    basketball: [
        { id: "nba", name: "NBA" },
        { id: "euroleague", name: "EuroLeague" },
    ]
};

const ContentManagement = () => {
    const { token } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [content, setContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingContent, setEditingContent] = useState(null);
    const [saving, setSaving] = useState(false);
    const [summarizing, setSummarizing] = useState(false);
    const [imageEditItem, setImageEditItem] = useState(null);

    const [formData, setFormData] = useState({
        title: "",
        title_sw: "",
        content: "",
        content_sw: "",
        summary: "",
        summary_sw: "",
        sport: "football",
        competition_id: "",
        content_type: "news",
        team: "",
        tags: "",
        image_url: "",
        is_featured: false,
        is_breaking: false
    });

    useEffect(() => {
        fetchContent();
    }, [searchQuery, selectedCategory, selectedStatus]);

    // Auto-open edit dialog when redirected from news curation with ?edit=<id>
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const editId = params.get("edit");
        if (!editId) return;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/content/${editId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                handleEdit(data);
                // Clear the query param so re-renders don't re-open
                navigate("/admin/content", { replace: true });
            } catch {
                toast.error("Draft not found");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const fetchContent = async () => {
        try {
            let url = `${API}/content?limit=50`;
            if (searchQuery) url += `&search=${searchQuery}`;
            if (selectedCategory && selectedCategory !== "all") url += `&category=${selectedCategory}`;
            if (selectedStatus && selectedStatus !== "all") url += `&status=${selectedStatus}`;
            
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setContent(response.data);
        } catch (error) {
            toast.error("Failed to fetch content");
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingContent(null);
        setFormData({
            title: "",
            title_sw: "",
            content: "",
            content_sw: "",
            summary: "",
            summary_sw: "",
            sport: "football",
            competition_id: "",
            content_type: "news",
            team: "",
            tags: "",
            image_url: "",
            is_featured: false,
            is_breaking: false
        });
        setIsDialogOpen(true);
    };

    const handleEdit = (item) => {
        setEditingContent(item);
        setFormData({
            title: item.title,
            title_sw: item.title_sw,
            content: item.content,
            content_sw: item.content_sw,
            summary: item.summary || "",
            summary_sw: item.summary_sw || "",
            sport: item.sport || "football",
            competition_id: item.competition_id || "",
            content_type: item.content_type || "news",
            team: item.team || "",
            tags: item.tags?.join(", ") || "",
            image_url: item.image_url || "",
            is_featured: item.is_featured,
            is_breaking: item.is_breaking || false
        });
        setIsDialogOpen(true);
    };

    const handleSave = async (publish = false) => {
        setSaving(true);
        try {
            const payload = {
                ...formData,
                tags: formData.tags.split(",").map(t => t.trim()).filter(Boolean)
            };
            if (publish) {
                payload.status = "published";
            }

            if (editingContent) {
                await axios.put(`${API}/content/${editingContent.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success(publish ? "Content published successfully" : "Content updated successfully");
            } else {
                await axios.post(`${API}/content`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Content created successfully");
            }
            setIsDialogOpen(false);
            fetchContent();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to save content");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this content?")) return;
        try {
            await axios.delete(`${API}/content/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Content deleted");
            fetchContent();
        } catch (error) {
            toast.error("Failed to delete content");
        }
    };

    const handlePublish = async (item) => {
        try {
            const newStatus = item.status === "published" ? "draft" : "published";
            await axios.put(`${API}/content/${item.id}`, { status: newStatus }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Content ${newStatus === "published" ? "published" : "unpublished"}`);
            fetchContent();
        } catch (error) {
            toast.error("Failed to update status");
        }
    };

    const handleSummarize = async (field) => {
        const textToSummarize = field === "sw" ? formData.content_sw : formData.content;
        if (!textToSummarize) {
            toast.error("Please enter content first");
            return;
        }
        setSummarizing(true);
        try {
            const response = await axios.post(`${API}/ai/summarize`, {
                content: textToSummarize,
                max_length: 156,
                language: field
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFormData(prev => ({
                ...prev,
                [field === "sw" ? "summary_sw" : "summary"]: response.data.summary
            }));
            toast.success(`Summary generated (${response.data.character_count} chars)`);
        } catch (error) {
            toast.error("Failed to generate summary");
        } finally {
            setSummarizing(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
            year: "numeric"
        });
    };

    return (
        <AdminLayout title="Content Management">
            <div className="space-y-6" data-testid="content-management">
                {/* Discover from Web — auto-populate articles from RSS/ESPN/DDG */}
                <DiscoverWidget defaultKind="article" />

                {/* Toolbar */}
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                    <div className="flex flex-wrap gap-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                            <Input
                                placeholder="Search content..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 bg-paper border-border text-white w-64"
                                data-testid="content-search"
                            />
                        </div>
                        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                            <SelectTrigger className="w-40 bg-paper border-border text-white" data-testid="category-filter">
                                <SelectValue placeholder="Category" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Categories</SelectItem>
                                {CATEGORIES.map(cat => (
                                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                            <SelectTrigger className="w-32 bg-paper border-border text-white" data-testid="status-filter">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="published">Published</SelectItem>
                                <SelectItem value="archived">Archived</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={handleCreate} className="btn-primary" data-testid="create-content-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Create Content
                    </Button>
                </div>

                {/* Content List */}
                {loading ? (
                    <div className="grid gap-4">
                        {[...Array(5)].map((_, i) => (
                            <Card key={i} className="bg-paper border-border animate-pulse">
                                <CardContent className="p-4 flex gap-4">
                                    <div className="w-32 h-20 bg-muted rounded" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-5 bg-muted rounded w-3/4" />
                                        <div className="h-4 bg-muted rounded w-1/2" />
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : content.length > 0 ? (
                    <div className="grid gap-4">
                        {content.map((item, idx) => (
                            <Card 
                                key={item.id} 
                                className="bg-paper border-border hover:border-border/80 transition-colors"
                                data-testid={`content-item-${idx}`}
                            >
                                <CardContent className="p-4">
                                    <div className="flex gap-4">
                                        <button
                                            type="button"
                                            onClick={() => setImageEditItem(item)}
                                            className="relative group shrink-0"
                                            title="Badilisha picha"
                                            data-testid={`change-image-${idx}`}
                                        >
                                            <img
                                                src={item.image_url || "https://images.unsplash.com/photo-1650289246926-ca8eaa0c3325?w=200"}
                                                alt={item.title}
                                                className="w-32 h-20 object-cover rounded"
                                            />
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 rounded transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                <div className="flex items-center gap-1 text-white text-xs font-bold">
                                                    <ImageIcon className="h-4 w-4" />
                                                    Badilisha
                                                </div>
                                            </div>
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-4">
                                                <div>
                                                    <h3 className="text-white font-medium truncate">
                                                        {item.title}
                                                    </h3>
                                                    <p className="text-white/50 text-sm truncate">
                                                        {item.title_sw}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <Badge 
                                                        className={
                                                            item.status === "published" 
                                                                ? "bg-electric/10 text-electric border-electric/30" 
                                                                : item.status === "draft"
                                                                ? "bg-solar/10 text-solar border-solar/30"
                                                                : "bg-white/10 text-white/50"
                                                        }
                                                    >
                                                        {item.status}
                                                    </Badge>
                                                    {item.is_featured && (
                                                        <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30">
                                                            Featured
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4 mt-3">
                                                <span className="text-white/40 text-xs">
                                                    {CATEGORIES.find(c => c.id === item.category)?.name}
                                                </span>
                                                <span className="text-white/40 text-xs">
                                                    {formatDate(item.created_at)}
                                                </span>
                                                <span className="text-white/40 text-xs flex items-center gap-1">
                                                    <Eye className="h-3 w-3" />
                                                    {item.views_web + item.views_sms + item.views_whatsapp}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-2 shrink-0">
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                onClick={() => handleEdit(item)}
                                                className="text-white/60 hover:text-white"
                                                data-testid={`edit-content-${idx}`}
                                            >
                                                <Edit className="h-4 w-4" />
                                            </Button>
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                onClick={() => handlePublish(item)}
                                                className={item.status === "published" ? "text-electric" : "text-solar"}
                                                data-testid={`publish-content-${idx}`}
                                            >
                                                {item.status === "published" ? "Unpublish" : "Publish"}
                                            </Button>
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                onClick={() => handleDelete(item.id)}
                                                className="text-red-400 hover:text-red-300"
                                                data-testid={`delete-content-${idx}`}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card className="bg-paper border-border">
                        <CardContent className="p-12 text-center">
                            <FileText className="h-16 w-16 text-white/20 mx-auto mb-4" />
                            <h3 className="text-white font-heading text-xl uppercase mb-2">No Content Found</h3>
                            <p className="text-white/50 mb-4">Create your first article to get started</p>
                            <Button onClick={handleCreate} className="btn-primary">
                                <Plus className="h-4 w-4 mr-2" />
                                Create Content
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {/* Create/Edit Dialog */}
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogContent className="bg-paper border-border max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-white font-heading uppercase">
                                {editingContent ? "Edit Content" : "Create Content"}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4" data-testid="content-form">
                            {/* English Fields */}
                            <div className="space-y-4">
                                <h4 className="text-electric font-heading uppercase text-sm">English</h4>
                                <div>
                                    <Label className="text-white">Title</Label>
                                    <Input
                                        value={formData.title}
                                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                                        className="bg-subtle border-border text-white"
                                        data-testid="title-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white">Content</Label>
                                    <Textarea
                                        value={formData.content}
                                        onChange={(e) => setFormData({...formData, content: e.target.value})}
                                        className="bg-subtle border-border text-white min-h-[200px]"
                                        data-testid="content-input"
                                    />
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <Label className="text-white">Summary (SMS)</Label>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleSummarize("en")}
                                            disabled={summarizing}
                                            className="text-electric text-xs"
                                        >
                                            <Sparkles className="h-3 w-3 mr-1" />
                                            AI Summarize
                                        </Button>
                                    </div>
                                    <Textarea
                                        value={formData.summary}
                                        onChange={(e) => setFormData({...formData, summary: e.target.value})}
                                        className="bg-subtle border-border text-white"
                                        maxLength={160}
                                        placeholder="Max 156 characters for SMS"
                                        data-testid="summary-input"
                                    />
                                    <p className="text-white/40 text-xs mt-1">{formData.summary.length}/156</p>
                                </div>
                            </div>

                            {/* Swahili Fields */}
                            <div className="space-y-4">
                                <h4 className="text-solar font-heading uppercase text-sm">Swahili</h4>
                                <div>
                                    <Label className="text-white">Kichwa (Title)</Label>
                                    <Input
                                        value={formData.title_sw}
                                        onChange={(e) => setFormData({...formData, title_sw: e.target.value})}
                                        className="bg-subtle border-border text-white"
                                        data-testid="title-sw-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-white">Makala (Content)</Label>
                                    <Textarea
                                        value={formData.content_sw}
                                        onChange={(e) => setFormData({...formData, content_sw: e.target.value})}
                                        className="bg-subtle border-border text-white min-h-[200px]"
                                        data-testid="content-sw-input"
                                    />
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <Label className="text-white">Muhtasari (Summary)</Label>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleSummarize("sw")}
                                            disabled={summarizing}
                                            className="text-solar text-xs"
                                        >
                                            <Sparkles className="h-3 w-3 mr-1" />
                                            AI Summarize
                                        </Button>
                                    </div>
                                    <Textarea
                                        value={formData.summary_sw}
                                        onChange={(e) => setFormData({...formData, summary_sw: e.target.value})}
                                        className="bg-subtle border-border text-white"
                                        maxLength={160}
                                        placeholder="Max 156 characters for SMS"
                                        data-testid="summary-sw-input"
                                    />
                                    <p className="text-white/40 text-xs mt-1">{formData.summary_sw.length}/156</p>
                                </div>
                            </div>

                            {/* Meta Fields */}
                            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <Label className="text-white">Category</Label>
                                    <Select 
                                        value={formData.category} 
                                        onValueChange={(v) => setFormData({...formData, category: v})}
                                    >
                                        <SelectTrigger className="bg-subtle border-border text-white" data-testid="category-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CATEGORIES.map(cat => (
                                                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-white">Tags (comma separated)</Label>
                                    <Input
                                        value={formData.tags}
                                        onChange={(e) => setFormData({...formData, tags: e.target.value})}
                                        className="bg-subtle border-border text-white"
                                        placeholder="e.g. premier league, transfer"
                                        data-testid="tags-input"
                                    />
                                </div>
                                <div>
                                    <ImageUploader
                                        label="Image / Video"
                                        value={formData.image_url}
                                        onChange={(url) => setFormData({...formData, image_url: url})}
                                        accept="any"
                                        purpose="article"
                                        testId="content-image-uploader"
                                    />
                                </div>
                            </div>

                            {/* Featured Toggle */}
                            <div className="md:col-span-2 flex items-center justify-between p-4 bg-subtle rounded">
                                <div>
                                    <Label className="text-white">Featured Article</Label>
                                    <p className="text-white/50 text-sm">Show in hero section</p>
                                </div>
                                <Switch
                                    checked={formData.is_featured}
                                    onCheckedChange={(c) => setFormData({...formData, is_featured: c})}
                                    data-testid="featured-switch"
                                />
                            </div>

                            {/* Actions */}
                            <div className="md:col-span-2 flex justify-end gap-3">
                                <Button
                                    variant="ghost"
                                    onClick={() => setIsDialogOpen(false)}
                                    className="text-white/60"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={() => handleSave(false)}
                                    disabled={saving || !formData.title || !formData.title_sw}
                                    variant="outline"
                                    className="border-electric/40 text-electric hover:bg-electric/10"
                                    data-testid="save-content-btn"
                                >
                                    {saving ? (
                                        <span className="animate-pulse">Saving...</span>
                                    ) : (
                                        <>
                                            <Save className="h-4 w-4 mr-2" />
                                            {editingContent ? "Save Draft" : "Create"}
                                        </>
                                    )}
                                </Button>
                                {editingContent && (
                                    <Button
                                        onClick={() => handleSave(true)}
                                        disabled={saving || !formData.title_sw || !formData.content_sw}
                                        className="bg-electric hover:bg-vodaredDark text-white"
                                        data-testid="publish-content-btn"
                                    >
                                        <Eye className="h-4 w-4 mr-2" />
                                        Save &amp; Publish
                                    </Button>
                                )}
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Quick Image Edit Dialog */}
                <EditImageDialog
                    open={!!imageEditItem}
                    onOpenChange={(o) => !o && setImageEditItem(null)}
                    contentId={imageEditItem?.id}
                    currentUrl={imageEditItem?.image_url}
                    onSaved={(newUrl) => {
                        setContent((prev) =>
                            prev.map((c) => (c.id === imageEditItem.id ? { ...c, image_url: newUrl } : c))
                        );
                        setImageEditItem(null);
                    }}
                />
            </div>
        </AdminLayout>
    );
};

export default ContentManagement;
